import ModalFormConta from "components/Features/Financeiro/FormConta/ModalFormConta";
import ModalFormPagarConta from "components/Features/Financeiro/FormConta/ModalFormPagarConta";
import { useLoading } from "components/GlobalLoading";
import NewTable from "components/NewTable";
import HeaderTable from "components/Page/HeaderTable";
import useContasRecorrentes from "core/features/conta-recorrente/hooks/useContasRecorrentes";
import useDeleteContaRecorrente from "core/features/conta-recorrente/hooks/useDeleteContaRecorrente";
import { ContaRecorrenteItem } from "core/features/conta-recorrente/typings";
import { OperacaoEnum } from "core/resources/enums/conta";
import { PermissaoEnum } from "core/resources/enums/permissao";
import { showConfirm } from "infra/helpers/alerta";
import {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { ModalContentConta } from "../../../../../ModalContentConta";
import ModalContasLancadas from "../ModalContasLancadas";
import ModalFormContaRecorrente from "../ModalFormContaRecorrente";
import ModalLancamento from "../ModalLancamento";
import useContaRecorrenteColumns from "../../hooks/useContaRecorrenteColumns";
import { ContaRecorrenteParams } from "core/features/conta-recorrente/typings/index";
import { Box, Flex, Text, Skeleton } from "@chakra-ui/react";
import InfiniteScroll from "react-infinite-scroll-component";
import InfiniteScrollLoader from "components/InfiniteScrollLoader";
import { useMemo } from "react";
import ModalComposicaoConta from "components/ModalComposicaoConta";
import useContasRecorrentesDashboard from "core/features/conta-recorrente/hooks/useContasRecorrentesDashboard";
import { formatMoney } from "infra/helpers/number";
import ExportMenu from "ui/components/ExportMenu";
import useDownloadRenderedPdf from "infra/helpers/useDownloadRenderedPdf";
import PDFContasRecorrentes from "../PDFContasRecorrentes/index";
import useContasRecorrentesPdf from "core/features/conta-recorrente/hooks/useContasRecorrentesPdf";
import { useCallback } from "react";
import ModalSelectFieldsRelatorio from "ui/components/ModalSelectFieldsRelatorio/index";
import moment from "moment";
import { exportExcelContasRecorrentes } from "../../util/helper";
import ButtonResumoPorCategoria from "../../../../../Button/ButtonResumoPorCategoria";
import {
  fieldsPDFContasRecorrentesColumns,
  ContasRecorrentesFields,
} from "core/features/relatorios/typings";

interface ContaRecorrentePageProps {
  operacao: OperacaoEnum;
  filtro: ContaRecorrenteParams;
  onChangeFiltro: (filtro: Partial<ContaRecorrenteParams>) => any;
  refetch?: () => any;
  hideTableShadow?: boolean;
}

const TableContaRecorrente = forwardRef(
  (
    {
      operacao,
      filtro,
      onChangeFiltro,
      refetch: refetchOuter,
      hideTableShadow,
    }: ContaRecorrentePageProps,
    ref?: React.ForwardedRef<{
      openModalContaRecorrente: () => any;
      closeModalContaRecorrente: () => any;
    }>
  ) => {
    const tableContainerRef = useRef(null);
    const columns = useContaRecorrenteColumns();

    const [exportType, setExportType] = useState<"pdf" | "excel" | undefined>();
    const [modalFormVisible, setModalFormVisible] = useState(false);
    const [modalResumoVisible, setModalResumoVisible] = useState(false);
    const [editId, setEditId] = useState<string | undefined>();
    const [editContaId, setEditContaId] = useState<string | undefined>();
    const [modalContasId, setModalContasId] = useState<string | undefined>();
    const [modalDetalheUltimaContaId, setModalDetalheUltimaContaId] = useState<
      string | undefined
    >();
    const [modalPagarContaId, setModalPagarContaId] = useState<
      string | undefined
    >();
    const [modalLancamentoId, setModalLancamentoId] = useState<
      string | undefined
    >();

    useImperativeHandle(ref, () => ({
      openModalContaRecorrente: () => setModalFormVisible(true),
      closeModalContaRecorrente: () => setModalFormVisible(false),
    }));

    const { isFetching: isFetchingPdf, refetch: refetchPdf } =
      useContasRecorrentesPdf(filtro);

    const { onDownload: onExportPdf } = useDownloadRenderedPdf({
      fileName: `${
        operacao === OperacaoEnum.Receita
          ? `Receitas recorrentes ${moment(filtro.mes, "YYYY-MM").format(
              "MM_YYYY"
            )}`
          : `Despesas recorrentes ${moment(filtro.mes, "YYYY-MM").format(
              "MM_YYYY"
            )}`
      }`,
      Component: PDFContasRecorrentes,
      loading: isFetchingPdf,
    });

    const _onExportPdf = useCallback(
      async ({
        selectedFields,
      }: {
        selectedFields: ContasRecorrentesFields[];
      }) => {
        const { data } = await refetchPdf();

        if (data) {
          onExportPdf({
            data,
            mes: filtro.mes,
            selectedFields,
          });
        }
      },
      [filtro.mes, onExportPdf, refetchPdf]
    );

    const _onExportExcel = useCallback(
      async ({
        selectedFields,
      }: {
        selectedFields: ContasRecorrentesFields[];
      }) => {
        const { data } = await refetchPdf();

        if (data) {
          exportExcelContasRecorrentes({
            data,
            mes: filtro.mes,
            operacao,
            selectedFields,
          });
        }
      },
      [filtro.mes, operacao, refetchPdf]
    );

    const {
      data,
      isLoading,
      refetch,
      fetchNextPage,
      isFetchingNextPage,
      hasNextPage,
    } = useContasRecorrentes(filtro);

    const { data: dashboard, isLoading: isLoadingDashboard } =
      useContasRecorrentesDashboard(filtro);

    const { mutate: deleteContaRecorrente, isLoading: isLoadingDelete } =
      useDeleteContaRecorrente();

    useEffect(() => {
      if (editId) {
        setModalFormVisible(true);
      }
    }, [editId]);

    useEffect(() => {
      if (!modalFormVisible) {
        setEditId(undefined);
      }
    }, [modalFormVisible]);

    const { update: setGlobalLoading } = useLoading(isLoadingDelete);

    const resetAll = () => {
      refetch();
      refetchOuter?.();
      setModalFormVisible(false);
      setModalContasId(undefined);
      setGlobalLoading(false);
    };

    const permissionBase =
      operacao === OperacaoEnum.Despesa
        ? PermissaoEnum.Backoffice.Financeiro.ContasPagar
        : PermissaoEnum.Backoffice.Financeiro.ContasReceber;

    const items = useMemo(() => {
      return (
        data?.pages.reduce(
          (agg: ContaRecorrenteItem[], item) => agg.concat(item.items ?? []),
          []
        ) ?? []
      );
    }, [data]);

    return (
      <Box ref={tableContainerRef}>
        <InfiniteScroll
          scrollableTarget="lista-lancamentos-conta-corrente"
          dataLength={items.length}
          next={() => {
            fetchNextPage();
          }}
          scrollThreshold={0.1}
          hasMore={!!hasNextPage}
          loader={isFetchingNextPage ? <InfiniteScrollLoader /> : null}
        >
          <NewTable<ContaRecorrenteItem>
            title={() => (
              <HeaderTable
                value={filtro.search ?? ""}
                onSearch={(search) => onChangeFiltro({ search })}
                children={
                  <Flex
                    justifyContent="flex-end"
                    gap={4}
                    flex={1}
                    alignItems="center"
                  >
                    {!isLoadingDashboard &&
                    dashboard?.resumoPorCategoria?.length ? (
                      <ButtonResumoPorCategoria
                        onClick={() => setModalResumoVisible(true)}
                      />
                    ) : null}

                    {items?.length && !isLoadingDashboard ? (
                      <ExportMenu
                        onExportPdf={() => setExportType("pdf")}
                        onExportarXlsx={() => setExportType("excel")}
                      />
                    ) : null}

                    {dashboard?.total ? (
                      <Text fontWeight="medium">
                        Total: {formatMoney(dashboard.total)}
                      </Text>
                    ) : null}

                    {isLoadingDashboard ? (
                      <Skeleton w="140px" h="24px" />
                    ) : null}
                  </Flex>
                }
              />
            )}
            actionsContainerRef={tableContainerRef}
            hideShadow={hideTableShadow}
            loading={isLoading}
            columns={columns}
            dataSource={items}
            actions={[
              {
                title: "Ver lançamento",
                permission: permissionBase.Editar,
                shouldRender: (row) => !!row.ultimaContaId,
                onClick: (row) =>
                  setModalDetalheUltimaContaId(row.ultimaContaId ?? undefined),
              },
              {
                title: "Editar",
                permission: permissionBase.Editar,
                onClick: (row) => setEditId(row.id),
              },
              {
                title: "Lançar",
                permission: permissionBase.Criar,
                onClick: async (row) => setModalLancamentoId(row.id),
              },
              {
                title: "Ver contas lançadas",
                permission: permissionBase.Visualizar,
                onClick: (row) => setModalContasId(row.id),
              },
              {
                title: "Excluir",
                permission: permissionBase.Excluir,
                onClick: async (row) => {
                  const { isConfirmed } = await showConfirm(
                    "Deseja realmente excluir essa conta recorrente? As contas já lançadas permancerão"
                  );

                  if (isConfirmed) {
                    deleteContaRecorrente(row.id, {
                      onSuccess: resetAll,
                    });
                  }
                },
              },
            ]}
          />
        </InfiniteScroll>

        {modalContasId ? (
          <ModalContasLancadas
            id={modalContasId}
            operacao={operacao}
            isOpen={!!modalContasId}
            onClose={() => setModalContasId(undefined)}
          />
        ) : null}

        {modalLancamentoId ? (
          <ModalLancamento
            id={modalLancamentoId}
            operacao={operacao}
            isOpen={!!modalLancamentoId}
            onClose={() => setModalLancamentoId(undefined)}
            refetch={refetch}
            mes={filtro.mes}
          />
        ) : null}

        {modalDetalheUltimaContaId ? (
          <ModalContentConta
            isOpenModal={!!modalDetalheUltimaContaId}
            onClose={() => setModalDetalheUltimaContaId(undefined)}
            contaId={modalDetalheUltimaContaId}
            onEdit={setEditContaId}
            onLiquidar={setModalPagarContaId}
            refetch={() => {
              refetch();
            }}
            operacao={operacao}
          />
        ) : null}

        {editContaId ? (
          <ModalFormConta
            operacao={operacao}
            id={editContaId}
            isOpen={!!editContaId}
            onClose={() => {
              setEditContaId(undefined);
            }}
            onSuccess={() => {
              refetch();
              setEditContaId(undefined);
            }}
            onSuccessImmediatelyAfterSave={false}
          />
        ) : null}

        {modalPagarContaId ? (
          <ModalFormPagarConta
            operacao={operacao}
            contaId={modalPagarContaId}
            isOpen={!!modalPagarContaId}
            onClose={() => setModalPagarContaId(undefined)}
          />
        ) : null}

        {modalFormVisible ? (
          <ModalFormContaRecorrente
            isOpen={modalFormVisible}
            onClose={() => setModalFormVisible(false)}
            operacao={operacao}
            id={editId}
            showRegisterAnother={!editId}
            refetch={refetch}
            onSuccess={() => {
              resetAll();
            }}
          />
        ) : null}

        {dashboard?.resumoPorCategoria && modalResumoVisible ? (
          <ModalComposicaoConta
            tableTitle="Categoria"
            data={{
              composicoes: dashboard?.resumoPorCategoria?.map((item) => ({
                valor: item.valor,
                categoriaFinanceiraId: item.id,
                categoriaFinanceira: {
                  breadcrumb: item.breadcrumb,
                  operacao: item.operacao ?? operacao,
                  nome: item.nome,
                  id: item.id,
                },
              })),
              total: dashboard?.total,
            }}
            onClose={() => setModalResumoVisible(false)}
            isOpen={modalResumoVisible}
            title="Resumo por categoria"
          />
        ) : null}

        <ModalSelectFieldsRelatorio
          title="Selecione as informações que aparecerão no arquivo exportado"
          visible={!!exportType}
          onClose={() => setExportType(undefined)}
          onSubmit={({ fields }) => {
            exportType === "pdf"
              ? _onExportPdf({
                  selectedFields: fields,
                })
              : _onExportExcel({
                  selectedFields: fields,
                });

            setExportType(undefined);
          }}
          fields={fieldsPDFContasRecorrentesColumns}
          includeAssinaturas={false}
          showFieldsTitle={false}
        />
      </Box>
    );
  }
);

export default TableContaRecorrente;
